import { useEffect, useState } from 'react';
import type { CurrencyApiMutation, CurrentUserQuery } from '../../graphql';
import { Currency, useCurrencyApiMutation } from '../../graphql';
import { useSetCurrencyRates } from '../../state/zustand';
import type { Notify } from '../notifications/ToastNotifications';
import { useNotify } from '../notifications/ToastNotifications';

// Apply returned rates whenever present and surface the advisory message when
// present — the two are independent. A degraded provider ships cached rates
// alongside an advisory; treating them as exclusive discards the rates and
// breaks every monetary conversion.
// spec: specs/constraints/currency-rate-degradation.md (Graceful Degradation, Advisory Message)
export function handleCurrencyApiResult(
  currencyData: CurrencyApiMutation['currencyApi'],
  notify: Notify,
  setCurrencyRates: ReturnType<typeof useSetCurrencyRates>,
): void {
  if (currencyData?.rates && currencyData.baseCurrency) {
    setCurrencyRates(currencyData.baseCurrency, currencyData.rates);
  }
  if (currencyData?.message) {
    notify(`Failed to fetch currency rates: ${currencyData.message}`, {
      toastId: 'currencyApi',
      autoClose: false,
      type: 'error',
    });
    console.error('Failed to fetch currency rates:', currencyData.message);
  }
}

export function useCurrencyApi(
  currentUser?: CurrentUserQuery['currentUser'],
): boolean {
  const [dataReady, setDataReady] = useState(false);
  const skip = !currentUser;
  const baseCurrency =
    currentUser?.organization?.defaultCurrency ??
    currentUser?.rootOrganization?.defaultCurrency ??
    Currency.EUR;

  const setCurrencyRates = useSetCurrencyRates();
  const [currencyApiMutation] = useCurrencyApiMutation();

  const notify = useNotify({
    toastId: 'currencyApi',
  });

  useEffect(() => {
    if (skip) return;

    let isMounted = true;

    const fetchCurrencyRates = async () => {
      try {
        const result = await currencyApiMutation({
          variables: { baseCurrency },
        });

        if (!isMounted) return;

        handleCurrencyApiResult(
          result.data?.currencyApi,
          notify,
          setCurrencyRates,
        );
      } catch (error) {
        if (!isMounted) return;

        const errorMessage =
          error instanceof Error ? error.message : String(error);
        notify(`Failed to fetch currency rates: ${errorMessage}`, {
          autoClose: false,
          type: 'error',
        });
        console.error('Failed to fetch currency rates:', error);
      } finally {
        if (isMounted) {
          setDataReady(true);
        }
      }
    };

    fetchCurrencyRates();

    return () => {
      isMounted = false;
    };
  }, [currencyApiMutation, skip, baseCurrency, notify, setCurrencyRates]);

  return dataReady;
}
